<template>
  <Teleport to="body">
    <div
      v-if="showTimeoutDialog"
      class="fixed inset-0 z-[100] flex flex-col items-center justify-center"
    >
      <div
        class="bg-[#141414] rounded-2xl p-8 max-w-[600px] w-full mx-4 text-center shadow-xl"
      >
        <h2 class="text-xl font-bold text-white mb-4">
          {{ $t("home.timeoutTitle", "Still There?") }}
        </h2>
        <p class="text-lg text-white mb-12">
          {{
            $t(
              "home.timeoutMessage",
              `You will be logged out in ${countdownSeconds} seconds`,
            )
          }}
        </p>
        <div class="flex justify-center gap-8">
          <button
            type="button"
            class="btn bg-playmaxx !text-pmblack focus:bg-playmaxx focus transition duration-300 focus:!text-white !w-[120px] !h-[40px] !text-lg text-bold"
            @click="onCancel"
          >
            {{ $t("home.timeoutContinue", "Cancel") }}
          </button>
          <button
            type="button"
            class="btn bg-gray-300 !text-gray-700 focus:bg-gray-400 transition duration-300 !w-[120px] !h-[40px] !text-lg text-bold"
            @click="onLogout"
          >
            {{ $t("home.timeoutLogout", "Log Out") }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
const user = useUserStore();
const { isKioskMode, kioskRfid } = useKioskMode();
const {
  timeoutMode,
  paramDeadline,
  inactivityDeadline,
  showTimeoutDialog,
  countdownSeconds,
  cancelDialog,
  resetInactivityDeadline,
  stop,
} = useKioskTimeout();

let pollInterval: ReturnType<typeof setInterval> | null = null;
let countdownInterval: ReturnType<typeof setInterval> | null = null;

const PARAM_POLL_MS = 3000;
const INACTIVITY_POLL_MS = 1000;

// Interaction listeners for inactivity reset
const onUserInteraction = () => {
  if (timeoutMode.value !== "inactivity") return;
  if (showTimeoutDialog.value) return;
  resetInactivityDeadline();
};

const bindInteractionListeners = () => {
  document.addEventListener("click", onUserInteraction, true);
  document.addEventListener("touchstart", onUserInteraction, true);
  document.addEventListener("keydown", onUserInteraction, true);
};

const unbindInteractionListeners = () => {
  document.removeEventListener("click", onUserInteraction, true);
  document.removeEventListener("touchstart", onUserInteraction, true);
  document.removeEventListener("keydown", onUserInteraction, true);
};

const clearAllTimers = () => {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
};

const performLogout = async () => {
  clearAllTimers();
  unbindInteractionListeners();
  stop();

  if (user.loggedIn) await user.logout();

  if (kioskRfid.value) {
    navigateTo(`/kiosk/${kioskRfid.value}`);
  } else {
    navigateTo("/kiosk");
  }
};

const startCountdown = () => {
  showTimeoutDialog.value = true;
  countdownSeconds.value = 5;
  countdownInterval = setInterval(async () => {
    countdownSeconds.value -= 1;
    if (countdownSeconds.value <= 0) {
      await performLogout();
    }
  }, 1000);
};

const startPolling = () => {
  clearAllTimers();

  const intervalMs =
    timeoutMode.value === "param" ? PARAM_POLL_MS : INACTIVITY_POLL_MS;

  pollInterval = setInterval(async () => {
    if (timeoutMode.value === "param") {
      if (Date.now() > paramDeadline.value) {
        await performLogout();
      }
    } else if (timeoutMode.value === "inactivity") {
      if (!showTimeoutDialog.value && Date.now() > inactivityDeadline.value) {
        startCountdown();
      }
    }
  }, intervalMs);
};

const onCancel = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  cancelDialog();
};

const onLogout = () => {
  performLogout();
};

// Kiosk user switching via wristband scan
if (isKioskMode.value && kioskRfid.value) {
  useKioskUserSwitch({
    rfid: kioskRfid.value as string,
    onSameUserRescan: () => cancelDialog(),
    onBeforeSwitch: () => stop(),
  });
}

// React to timeoutMode changes (set by pages)
watch(timeoutMode, (mode) => {
  if (mode) {
    if (mode === "inactivity") {
      bindInteractionListeners();
    }
    startPolling();
  } else {
    clearAllTimers();
    unbindInteractionListeners();
  }
});

// On mount, resume if a mode is already active
onMounted(() => {
  if (timeoutMode.value) {
    if (timeoutMode.value === "inactivity") {
      bindInteractionListeners();
    }
    startPolling();
  }
});

onUnmounted(() => {
  clearAllTimers();
  unbindInteractionListeners();
});
</script>
